// Import React library
import React from 'react';

/**
 * LoadingSpinnerProps - Configuration options for the loading spinner
 * This component shows users that something is loading/processing
 */
interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';    // How big the spinner should be (small, medium, large)
  message?: string;             // Optional text to show below the spinner
  className?: string;           // Additional CSS classes for custom styling
}

/**
 * LoadingSpinner Component
 * A reusable spinning circle animation that indicates something is loading
 * Used throughout the app when waiting for data from the server
 * 
 * The spinner is a circle with most of it gray, but the top part colored blue
 * The "animate-spin" CSS class makes it rotate continuously
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'md',                  // Default to medium size if not specified
  message = '',       // Default message if none provided
  className = ''                // Default to no extra classes
}) => {
  // Define different spinner sizes - these are CSS classes that set width and height
  const sizeClasses = {
    sm: 'h-4 w-4',               // Small: 16px x 16px
    md: 'h-8 w-8',               // Medium: 32px x 32px  
    lg: 'h-12 w-12'              // Large: 48px x 48px
  };

  // Define different container padding based on spinner size
  // Larger spinners get more space around them
  const containerClasses = {
    sm: 'p-2',                   // Small padding for small spinner
    md: 'p-8',                   // Medium padding for medium spinner
    lg: 'p-12'                   // Large padding for large spinner
  };

  return (
    <div className={`relative flex items-center justify-center ${containerClasses[size]} ${className}`}>
      <div className="flex flex-col items-center">
        <div className={`animate-spin rounded-full border-4 border-secondary-200 border-t-primary-600 ${sizeClasses[size]}`} />
        {message && (
          <p className="mt-2 text-sm text-secondary-600 animate-pulse text-center">{message}</p>
        )}
      </div>
    </div>
  );
};

// Export so other components can import and use this spinner
export default LoadingSpinner;